import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Mail, Copy, UserPlus, Trash2, Building2, Send, GitMerge, ShieldOff } from "lucide-react";
import { useStepUp } from "@/components/auth/StepUp";
import { HealthScoreBadge } from "@/components/HealthScoreBadge";
import { TeamChips } from "@/components/customers/TeamChips";
import { AccentDot } from "@/components/customers/AccentDot";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle } from "lucide-react";

function DupWarning({ dup, onInvite }) {
  return (
    <div className="mt-2 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-900" data-testid="duplicate-company-warning">
      <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
      <div><b>{dup.company}</b> already exists — <span className="font-mono">{dup.email}</span> ({dup.name}, {dup.driver_count ?? 0} drivers).
        {onInvite ? <> <button type="button" onClick={onInvite} className="underline font-medium">Invite that account instead</button>, or</> : " Use a different email only if this is truly a second account, or"} <Link to={`/customers/${dup.id}`} className="underline font-medium">open its profile</Link>.</div>
    </div>
  );
}
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";

export default function Customers() {
  const requireStepUp = useStepUp();
  const [customers, setCustomers] = useState([]);
  const [health, setHealth] = useState({});
  const [invites, setInvites] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", company: "", role: "customer" });
  const [admins, setAdmins] = useState([]);
  const { user: me } = useAuth();
  const [lastLink, setLastLink] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ email: "", name: "", company: "" });
  const [sisterPrompt, setSisterPrompt] = useState(null);

  const addCustomer = async (e, linkExisting = false) => {
    e?.preventDefault?.();
    if (!linkExisting && addDup && !window.confirm(`"${addDup.company}" already exists (${addDup.email}). Create a second account for this company anyway?`)) return;
    try {
      const { data } = await api.post("/customers", { ...addForm, force: !!addDup, link_existing: linkExisting });
      toast.success(data.linked_login ? `${data.company} created as a sister company — ${data.linked_login} can switch to it from the sidebar` : `${data.company || data.name} created — add drivers & certificates, then invite when ready`);
      setAddOpen(false); setSisterPrompt(null); setAddForm({ email: "", name: "", company: "" }); load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d?.code === "email_exists") { setSisterPrompt(d); return; }
      toast.error(formatApiError(err));
    }
  };

  const inviteExisting = (c) => {
    setForm({ email: c.email, name: c.name, company: c.company || "", role: "customer" });
    setOpen(true);
  };

  const [mergeSource, setMergeSource] = useState(null);
  const [mergeTarget, setMergeTarget] = useState("");
  const doMerge = async () => {
    const target = customers.find((c) => c.id === mergeTarget);
    if (!target) return;
    if (!window.confirm(`Merge "${mergeSource.company || mergeSource.name}" (${mergeSource.email}) INTO "${target.company || target.name}" (${target.email})?\n\nAll drivers, certificates, requests, messages and consultant records move to ${target.email}. The login ${mergeSource.email} will be removed. This cannot be undone.`)) return;
    try {
      const { data } = await api.post(`/customers/${mergeTarget}/merge`, { source_id: mergeSource.id });
      toast.success(`Merged · moved ${data.moved.drivers} drivers, ${data.moved.certificates} certificates, ${data.moved.requests} requests`);
      setMergeSource(null); setMergeTarget(""); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const load = useCallback(async () => {
    try {
      const [{ data: c }, { data: i }, { data: a }] = await Promise.all([api.get("/customers"), api.get("/invites"), api.get("/admins")]);
      setCustomers(c); setInvites(i); setAdmins(a);
      api.get("/health-score/all").then((r) => setHealth(r.data)).catch(() => {});
    } catch (err) { toast.error(formatApiError(err)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const dupFor = (company, email) => {
    const n = (company || "").trim().toLowerCase();
    return n ? customers.find((c) => (c.company || "").trim().toLowerCase() === n && c.email !== (email || "").toLowerCase()) : null;
  };
  const inviteDup = form.role === "customer" ? dupFor(form.company, form.email) : null;
  const addDup = dupFor(addForm.company, addForm.email);

  const invite = async (e) => {
    e.preventDefault();
    if (inviteDup && !window.confirm(`"${inviteDup.company}" already exists (${inviteDup.email}). Create a second account for this company anyway?`)) return;
    try {
      const { data } = await api.post("/invites", { ...form, force: !!inviteDup, origin_url: window.location.origin });
      setLastLink(data);
      if (data.email_status?.sent) toast.success(`Invite emailed to ${data.email}`);
      else toast.warning(`Invite created but email failed — copy the link below. ${data.email_status?.error || ""}`);
      setForm({ email: "", name: "", company: "", role: "customer" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const cancel = async (id) => {
    try { await api.delete(`/invites/${id}`); toast.success("Invite cancelled"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const resend = async (id) => {
    try { await api.post(`/invites/${id}/resend`, { origin_url: window.location.origin }); toast.success("Invite email re-sent"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const resetTwoFactor = async (u) => {
    if (!window.confirm(`Reset the sign-in method for ${u.name}?\n\nThis clears their authenticator app, email codes and trusted devices. At their next sign-in they'll choose Authenticator or Email code again, and they'll get an email about this change.`)) return;
    try { await api.post(`/auth/2fa/reset/${u.id}`); toast.success(`Sign-in method reset for ${u.name} — they can sign in with just their password and pick a new method`); } catch (err) { toast.error(formatApiError(err)); }
  };
  const removeCustomer = async (c) => {
    if (!window.confirm(`Delete ${c.company || c.name} (${c.email})?\n\nThis permanently removes their login, drivers, test history, certificates, report requests and messages.`)) return;
    if (!(await requireStepUp())) return;
    try { const { data } = await api.delete(`/customers/${c.id}`); toast.success(`Customer deleted (${data.drivers_removed} drivers removed)`); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const removeAdmin = async (a) => {
    if (!window.confirm(`Remove admin access for ${a.email}?`)) return;
    try { await api.delete(`/admins/${a.id}`); toast.success("Admin removed"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const copyLink = async () => {
    if (!lastLink) return;
    const link = window.location.origin + `/accept-invite?token=${lastLink.token}`;
    try { await navigator.clipboard.writeText(link); toast.success("Copied"); } catch { toast.error("Copy failed"); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="customers-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <div className="tag-mono text-blue-700 mb-1">ADMIN</div>
          <h1 className="text-2xl sm:text-3xl font-extrabold font-heading text-slate-900">Customers &amp; Invitations</h1>
          <p className="text-slate-600 mt-1">Create a customer to start loading their drivers and certificates, then invite them to the portal when ready.</p>
        </div>
        <div className="flex flex-wrap gap-2">
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" data-testid="add-customer-button"><Building2 className="h-4 w-4 mr-2" /> Add Customer</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add Customer (no invite yet)</DialogTitle></DialogHeader>
            <form onSubmit={addCustomer} className="space-y-3">
              <p className="text-sm text-slate-600">Creates the company record so you can add drivers and certificates on their behalf. They will not be able to log in until you send an invite.</p>
              <div><Label>Company</Label><Input required value={addForm.company} onChange={(e) => setAddForm({ ...addForm, company: e.target.value })} data-testid="add-customer-company-input" />
                {addDup && <DupWarning dup={addDup} onInvite={() => { setAddOpen(false); inviteExisting(addDup); }} />}</div>
              <div><Label>Contact name</Label><Input required value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} data-testid="add-customer-name-input" /></div>
              <div><Label>Contact email</Label><Input required type="email" value={addForm.email} onChange={(e) => { setAddForm({ ...addForm, email: e.target.value }); setSisterPrompt(null); }} data-testid="add-customer-email-input" /></div>
              {sisterPrompt && (
                <div className="rounded-md border border-violet-300 bg-violet-50 p-3 text-sm space-y-2" data-testid="sister-company-prompt">
                  <div className="text-violet-900"><b>{addForm.email}</b> already signs in as <b>{sisterPrompt.existing?.company || sisterPrompt.existing?.name}</b>.</div>
                  <div className="text-xs text-violet-800">Create <b>{addForm.company}</b> as a <b>sister company</b>: no second login — that person (and anyone they invite) switches between companies from the sidebar. Statements & notices for the new company go to <span className="font-mono">{addForm.email.replace("@", `+${(addForm.company || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 24)}@`)}</span>, which lands in the same inbox on Gmail / Google Workspace / Microsoft 365.</div>
                  <Button type="button" size="sm" className="bg-violet-700 hover:bg-violet-800 text-white" onClick={(e) => addCustomer(e, true)} data-testid="create-sister-company-button">Create sister company &amp; link login</Button>
                </div>
              )}
              <DialogFooter><Button type="submit" className="bg-blue-700 hover:bg-blue-800" data-testid="add-customer-submit-button">Create Customer</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-blue-700 hover:bg-blue-800" data-testid="invite-customer-button">
              <UserPlus className="h-4 w-4 mr-2" /> Invite User
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New Invitation</DialogTitle></DialogHeader>
            <form onSubmit={invite} className="space-y-3">
              <div>
                <Label>Role</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  {[["customer", "Customer", "Fleet / company user"], ["admin", "Administrator", "Full Safer Highways access"]].map(([v, l, d]) => (
                    <button type="button" key={v} onClick={() => setForm({ ...form, role: v })} data-testid={`invite-role-${v}`}
                      className={`text-left rounded-md border p-3 transition-colors ${form.role === v ? "border-blue-600 bg-blue-50" : "border-slate-200 hover:bg-slate-50"}`}>
                      <div className="font-medium text-sm">{l}</div><div className="text-xs text-slate-500">{d}</div>
                    </button>
                  ))}
                </div>
              </div>
              <div><Label>Email</Label><Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="invite-email-input" /></div>
              <div><Label>Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="invite-name-input" /></div>
              {form.role === "customer" && <div><Label>Company</Label><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="invite-company-input" />
                {inviteDup && <DupWarning dup={inviteDup} />}</div>}
              <DialogFooter><Button type="submit" className="bg-blue-700 hover:bg-blue-800" data-testid="invite-submit-button">{form.role === "admin" ? "Invite Administrator" : "Invite Customer"}</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Dialog open={!!mergeSource} onOpenChange={(o) => !o && setMergeSource(null)}>
        <DialogContent data-testid="merge-dialog">
          <DialogHeader><DialogTitle>Merge duplicate customer</DialogTitle></DialogHeader>
          {mergeSource && (
            <div className="space-y-3 text-sm">
              <p className="text-slate-600">Everything under <b>{mergeSource.company || mergeSource.name}</b> <span className="font-mono text-xs">({mergeSource.email})</span> — {mergeSource.driver_count} drivers, {mergeSource.cert_count} certificates — will move to the account you choose. The duplicate login is then removed.</p>
              <div><Label>Keep this account</Label>
                <Select value={mergeTarget} onValueChange={setMergeTarget}><SelectTrigger data-testid="merge-target-select"><SelectValue placeholder="Select the account to keep…" /></SelectTrigger>
                  <SelectContent>{customers.filter((c) => c.id !== mergeSource.id).map((c) => <SelectItem key={c.id} value={c.id}>{c.company || c.name} · {c.email}</SelectItem>)}</SelectContent></Select></div>
              <DialogFooter><Button variant="outline" onClick={() => setMergeSource(null)}>Cancel</Button><Button disabled={!mergeTarget} onClick={doMerge} className="bg-blue-700 hover:bg-blue-800" data-testid="merge-confirm-button"><GitMerge className="h-4 w-4 mr-2" />Merge</Button></DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {lastLink && (
        <Card className="p-4 mb-4 bg-blue-50 border-blue-200" data-testid="last-invite-link">
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <div className="tag-mono text-blue-700 mb-1">{lastLink.email_status?.sent ? `INVITE EMAILED TO ${lastLink.email.toUpperCase()} · backup link` : "EMAIL FAILED — SHARE THIS LINK MANUALLY"}</div>
              <div className="font-mono text-xs text-slate-800 truncate">
                {window.location.origin}/accept-invite?token={lastLink.token}
              </div>
            </div>
            <Button variant="outline" onClick={copyLink} data-testid="copy-invite-link-button"><Copy className="h-4 w-4 mr-2" />Copy</Button>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <div className="px-5 py-3 border-b border-slate-200 tag-mono text-slate-500">ADMINISTRATORS</div>
          <Table>
            <TableBody>
              {admins.map((a) => (
                <TableRow key={a.id} data-testid={`admin-row-${a.id}`}>
                  <TableCell className="font-medium">{a.name}{a.id === me?.id && <Badge className="ml-2 bg-blue-50 text-blue-700 border border-blue-200">you</Badge>}</TableCell>
                  <TableCell className="font-mono text-xs">{a.email}</TableCell>
                  <TableCell className="text-right">{a.id !== me?.id && <><Button variant="ghost" size="icon" title="Reset authenticator" onClick={() => resetTwoFactor(a)} data-testid={`reset-2fa-admin-${a.id}`}><ShieldOff className="h-4 w-4 text-amber-600" /></Button><Button variant="ghost" size="icon" onClick={() => removeAdmin(a)} data-testid={`delete-admin-${a.id}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button></>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
        <Card>
          <div className="px-5 py-3 border-b border-slate-200 tag-mono text-slate-500">CUSTOMERS</div>
          <Table>
            <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Company</TableHead><TableHead>Health</TableHead><TableHead>Portal</TableHead><TableHead>Team</TableHead><TableHead className="text-right">Drivers</TableHead><TableHead className="text-right">Open requests</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>
              {customers.length === 0 && <TableRow><TableCell colSpan={9} className="text-center py-6 text-slate-500">No customers yet.</TableCell></TableRow>}
              {customers.map((c) => {
                const pendingInvite = !c.activated && invites.find((i) => i.email === c.email && !i.accepted_at && !i.cancelled_at);
                return (
                <TableRow key={c.id} data-testid={`customer-row-${c.id}`}><TableCell className="font-medium"><Link to={`/customers/${c.id}`} className="text-blue-700 hover:underline" data-testid={`customer-profile-link-${c.id}`}>{c.name}</Link></TableCell><TableCell className="font-mono text-xs">{c.email}</TableCell><TableCell><div className="flex items-center gap-2"><AccentDot customer={c} onChanged={(id, v) => setCustomers((cs) => cs.map((x) => (x.id === id ? { ...x, accent_color: v } : x)))} />{c.company || "—"}</div></TableCell>
                  <TableCell>{health[c.id] ? <HealthScoreBadge compact customerId={c.id} company={c.company || c.name} summary={health[c.id]} /> : <span className="text-xs text-slate-400">…</span>}</TableCell>
                  <TableCell>
                    {c.activated ? <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid={`customer-status-${c.id}`}>Active</Badge>
                      : pendingInvite ? <Badge className="bg-indigo-50 text-indigo-700 border border-indigo-200" data-testid={`customer-status-${c.id}`}>Invited</Badge>
                      : <button type="button" onClick={() => inviteExisting(c)} className="inline-flex items-center gap-1 text-xs text-amber-800 bg-amber-50 border border-amber-300 rounded-full px-2 py-0.5 hover:bg-amber-100" data-testid={`customer-invite-${c.id}`}><Send className="h-3 w-3" />Not invited · Invite</button>}
                  </TableCell>
                  <TableCell><TeamChips customer={c} /></TableCell>
                  <TableCell className="text-right"><Link to={`/drivers?customer=${c.id}`} className="font-mono text-blue-700 hover:underline" data-testid={`customer-drivers-${c.id}`}>{c.driver_count ?? 0} →</Link></TableCell>
                  <TableCell className="text-right"><Link to="/reports" className="font-mono text-blue-700 hover:underline">{c.open_requests ?? 0}</Link></TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button variant="ghost" size="icon" title="Merge into another customer" onClick={() => { setMergeSource(c); setMergeTarget(""); }} data-testid={`merge-customer-${c.id}`}><GitMerge className="h-4 w-4 text-slate-600" /></Button>
                    <Button variant="outline" size="sm" className="h-7 border-amber-300 text-amber-800 bg-amber-50 hover:bg-amber-100" title="Reset sign-in method — clears authenticator, email codes and trusted devices so they can get back in" onClick={() => resetTwoFactor(c)} data-testid={`reset-2fa-customer-${c.id}`}><ShieldOff className="h-3.5 w-3.5 mr-1" />Reset sign-in</Button>
                    <Button variant="ghost" size="icon" onClick={() => removeCustomer(c)} data-testid={`delete-customer-${c.id}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>

        <Card>
          <div className="px-5 py-3 border-b border-slate-200 tag-mono text-slate-500">INVITATIONS</div>
          <Table>
            <TableHeader><TableRow><TableHead>Email</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {invites.length === 0 && <TableRow><TableCell colSpan={3} className="text-center py-6 text-slate-500">No invites.</TableCell></TableRow>}
              {invites.map((i) => {
                const status = i.accepted_at ? "accepted" : i.cancelled_at ? "cancelled" : "pending";
                const badgeCls = {
                  accepted: "bg-emerald-50 text-emerald-700 border-emerald-200",
                  cancelled: "bg-slate-100 text-slate-600 border-slate-200",
                  pending: "bg-indigo-50 text-indigo-700 border-indigo-200",
                }[status];
                return (
                  <TableRow key={i.id}>
                    <TableCell className="font-mono text-xs">{i.email}<div className="text-slate-500">{i.name} · {i.role === "admin" ? <span className="text-blue-700 font-semibold">Administrator</span> : i.company}</div>
                      {status === "pending" && (i.email_sent_at ? <div className="text-emerald-700" data-testid={`invite-emailed-${i.id}`}>Emailed {new Date(i.email_sent_at).toLocaleString()}</div> : <div className="text-amber-700" data-testid={`invite-not-emailed-${i.id}`}>Not emailed{i.email_error ? ` · ${i.email_error}` : ""}</div>)}
                    </TableCell>
                    <TableCell><Badge className={`${badgeCls} border capitalize`}>{status}</Badge></TableCell>
                    <TableCell className="text-right">
                      {status === "pending" && (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => resend(i.id)} data-testid={`resend-invite-${i.id}`}>
                            <Mail className="h-4 w-4 mr-1" /> Resend email
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/accept-invite?token=${i.token}`); toast.success("Link copied"); }} data-testid={`copy-invite-${i.id}`}>
                            <Copy className="h-4 w-4 mr-1" /> Copy link
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => cancel(i.id)} data-testid={`cancel-invite-${i.id}`}>Cancel</Button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      </div>
    </div>
  );
}
